/**
 * PromptTemplatesAdmin Component
 *
 * Purpose: Admin interface for managing airline-specific LLM prompt templates
 *
 * Features:
 * - View prompt template list with filtering (use_case, active status)
 * - Create new prompt templates (from scratch or copy from default)
 * - Edit prompt templates with syntax highlighting
 * - Activate/deactivate templates
 * - Delete templates (with confirmation)
 * - Version history and template preview
 *
 * Sprint: 4 (Airline Customization)
 * Author: Edward Berger + AI Assistant
 * Date: 2025-11-01
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useAirline } from '../../contexts/AirlineContext';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  CheckCircle,
  XCircle,
  Loader,
  Copy,
  FileText,
  Globe,
  Tag,
  Clock,
  Users,
} from 'lucide-react';

// =====================================================================
// Constants
// =====================================================================

// Well-known UUID for system-wide resources
const SYSTEM_ORG_ID = '00000000-0000-0000-0000-000000000000';

// =====================================================================
// Template Type Descriptions
// =====================================================================

const TEMPLATE_INFO: Record<string, { label: string; description: string; variables: string[] }> = {
  pilot_evidence_extraction: {
    label: 'Pilot Evidence Extraction (Pass 1)',
    description: 'Extracts evidence from transcript chunks (2000 words each). Customize to emphasize airline-specific behaviors and competency definitions.',
    variables: ['{{transcriptChunk}}', '{{#each competencies}}', '{{weather_context}}'],
  },
  pilot_assessment: {
    label: 'Pilot Competency Assessment (Pass 2)',
    description: 'Generates holistic competency assessment from aggregated evidence. Customize to incorporate airline SOPs, scoring standards, and cultural terminology.',
    variables: ['{{competency}}', '{{competencyName}}', '{{sessionType}}', '{{duration}}', '{{scenario}}', '{{sop_context}}', '{{#each evidence}}', '{{weather_context}}'],
  },
  instructor_assessment: {
    label: 'Instructor Assessment (ORCA360 Senior Coach)',
    description: 'Comprehensive CBTA/EBT instructor effectiveness evaluation using the ORCA360 Senior Instructor Coach methodology. Evaluates IEC1-5 criteria with evidence-based coaching, grading calibration, and facilitation vs. instruction balance assessment. Implements ICAO Doc 9995, EASA, and FAA standards.',
    variables: ['{{criterion}}', '{{criterionName}}', '{{sessionType}}', '{{aircraftType}}', '{{trainingPhase}}', '{{duration}}', '{{scenario}}', '{{#each evidence}}'],
  },
  evidence_validation: {
    label: 'Evidence Validation (Anti-Hallucination)',
    description: 'Validates that AI-generated evidence actually appears in source transcript. Rarely needs customization.',
    variables: ['{{claimedEvidence}}', '{{transcriptContext}}'],
  },
  sop_qa: {
    label: 'SOP Question & Answer',
    description: 'Answers questions about airline SOPs using pgvector semantic search. Customize for airline-specific citation style and tone.',
    variables: ['{{airline_name}}', '{{question}}', '{{sop_context}}'],
  },
  event_detection: {
    label: 'Event Detection',
    description: 'Detects critical events in transcripts (e.g., Go-Around, TCAS). Customize for airline-specific event definitions.',
    variables: ['{{transcriptChunk}}', '{{eventTypes}}'],
  },
  rca_analysis: {
    label: 'Root-Cause Analysis (Pass 2.5)',
    description: 'Generates detailed 5-Why root cause analysis for deficient or below-standard competencies (score ≤3). Runs after assessment generation (Pass 2.5). Identifies knowledge/skill/attitude gaps and proposes corrective actions. Customize for airline-specific RCA methodology.',
    variables: ['{{competency}}', '{{competencyName}}', '{{score}}', '{{#each evidence}}', '{{preliminaryAnalysis}}', '{{sessionType}}', '{{scenario}}', '{{aircraftType}}', '{{trainingPhase}}', '{{relatedEvents}}', '{{sop_context}}'],
  },
  smart_goals: {
    label: 'SMART Improvement Goals (Pass 2.6)',
    description: 'Generates actionable SMART (Specific, Measurable, Achievable, Relevant, Time-bound) improvement goals for competencies scoring below 4. Runs after RCA (Pass 2.6). Integrates root-cause insights into development plans with success criteria and follow-up actions. Customize for airline-specific goal-setting methodology and resources.',
    variables: ['{{competency}}', '{{competencyName}}', '{{score}}', '{{#each evidence}}', '{{rcaOutput}}', '{{preliminaryAnalysis}}', '{{sessionType}}', '{{scenario}}', '{{aircraftType}}', '{{trainingPhase}}', '{{relatedEvents}}', '{{sop_context}}'],
  },
};

// =====================================================================
// Types & Interfaces
// =====================================================================

interface PromptTemplate {
  id: string;
  org_id: string;
  name: string;
  use_case: 'pilot_evidence_extraction' | 'pilot_assessment' | 'instructor_assessment' | 'sop_qa' | 'event_detection' | 'evidence_validation' | 'rca_analysis' | 'smart_goals';
  role: 'system' | 'user' | 'tool';
  template: string;
  version: string;
  is_active: boolean;
  description: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface FilterState {
  use_case: string;
  status: string;
}

// =====================================================================
// Main Component
// =====================================================================

export const PromptTemplatesAdmin: React.FC = () => {
  const { user } = useAuth();
  const { selectedAirline } = useAirline();

  // State
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FilterState>({
    use_case: 'all',
    status: 'active',
  });
  const [organizationId, setOrganizationId] = useState<string | null>(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<PromptTemplate | null>(null);

  // Form state
  const [templateForm, setTemplateForm] = useState({
    name: '',
    use_case: 'assessment' as PromptTemplate['use_case'],
    role: 'user' as PromptTemplate['role'],
    template: '',
    version: 'v1.0',
    description: '',
    is_active: true,
  });
  const [saving, setSaving] = useState(false);

  // Derive organization_id from the selected airline (authoritative source)
  useEffect(() => {
    if (selectedAirline?.airline_id) {
      setOrganizationId(selectedAirline.airline_id);
    }
  }, [selectedAirline?.airline_id]);

  // ===================================================================
  // Load templates
  // ===================================================================

  useEffect(() => {
    loadTemplates();
  }, [filters]);

  const loadTemplates = async () => {
    setLoading(true);

    try {
      let query = supabase
        .from('prompt_templates')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.use_case !== 'all') {
        query = query.eq('use_case', filters.use_case);
      }

      if (filters.status === 'active') {
        query = query.eq('is_active', true);
      } else if (filters.status === 'inactive') {
        query = query.eq('is_active', false);
      }

      const { data, error } = await query;

      if (error) {
        console.error('[PromptTemplatesAdmin] Load error:', error);
        alert('Failed to load prompt templates. Please try again.');
        return;
      }

      setTemplates(data || []);
    } catch (error) {
      console.error('[PromptTemplatesAdmin] Unexpected error:', error);
    } finally {
      setLoading(false);
    }
  };

  // ===================================================================
  // Filter templates
  // ===================================================================

  const filteredTemplates = templates.filter((template) => {
    const matchesSearch =
      searchQuery === '' ||
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesSearch;
  });

  // ===================================================================
  // Create template
  // ===================================================================

  const handleCreate = async () => {
    if (!user) {
      alert('Please ensure you are logged in.');
      return;
    }

    if (!organizationId) {
      alert('Organization ID not loaded. Please refresh the page.');
      return;
    }

    if (!templateForm.name.trim()) {
      alert('Please enter a template name.');
      return;
    }

    if (!templateForm.template.trim()) {
      alert('Please enter template content.');
      return;
    }

    setSaving(true);

    try {
      const { error } = await supabase
        .from('prompt_templates')
        .insert({
          name: templateForm.name,
          use_case: templateForm.use_case,
          role: templateForm.role,
          template: templateForm.template,
          version: templateForm.version,
          description: templateForm.description || null,
          is_active: templateForm.is_active,
          created_by: user.id,
          org_id: organizationId,
        })
        .select()
        .single();

      if (error) {
        console.error('[PromptTemplatesAdmin] Create error:', error);
        alert('Failed to create prompt template.');
        return;
      }

      alert(`Template "${templateForm.name}" created successfully!`);
      setShowCreateModal(false);
      resetTemplateForm();
      loadTemplates();
    } catch (error) {
      console.error('[PromptTemplatesAdmin] Create error:', error);
      alert('An unexpected error occurred during creation.');
    } finally {
      setSaving(false);
    }
  };

  // ===================================================================
  // Update template
  // ===================================================================

  const handleUpdate = async () => {
    if (!selectedTemplate) return;

    if (!templateForm.name.trim()) {
      alert('Please enter a template name.');
      return;
    }

    if (!templateForm.template.trim()) {
      alert('Please enter template content.');
      return;
    }

    setSaving(true);

    try {
      const { error } = await supabase
        .from('prompt_templates')
        .update({
          name: templateForm.name,
          use_case: templateForm.use_case,
          role: templateForm.role,
          template: templateForm.template,
          version: templateForm.version,
          description: templateForm.description || null,
          is_active: templateForm.is_active,
        })
        .eq('id', selectedTemplate.id);

      if (error) {
        console.error('[PromptTemplatesAdmin] Update error:', error);
        alert('Failed to update prompt template.');
        return;
      }

      alert(`Template "${templateForm.name}" updated successfully!`);
      setShowEditModal(false);
      setSelectedTemplate(null);
      resetTemplateForm();
      loadTemplates();
    } catch (error) {
      console.error('[PromptTemplatesAdmin] Update error:', error);
      alert('An unexpected error occurred during update.');
    } finally {
      setSaving(false);
    }
  };

  // ===================================================================
  // Delete template
  // ===================================================================

  const handleDelete = async (template: PromptTemplate) => {
    if (!confirm(`Delete template "${template.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('prompt_templates')
        .delete()
        .eq('id', template.id);

      if (error) {
        console.error('[PromptTemplatesAdmin] Delete error:', error);
        alert('Failed to delete template.');
        return;
      }

      alert(`Template "${template.name}" deleted successfully!`);
      loadTemplates();
    } catch (error) {
      console.error('[PromptTemplatesAdmin] Delete error:', error);
      alert('An unexpected error occurred during deletion.');
    }
  };

  // ===================================================================
  // Toggle active status
  // ===================================================================

  const handleToggleActive = async (template: PromptTemplate, e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      const { error } = await supabase
        .from('prompt_templates')
        .update({ is_active: !template.is_active })
        .eq('id', template.id);

      if (error) {
        console.error('[PromptTemplatesAdmin] Toggle error:', error);
        alert('Failed to toggle template status.');
        return;
      }

      loadTemplates();
    } catch (error) {
      console.error('[PromptTemplatesAdmin] Toggle error:', error);
    }
  };

  // ===================================================================
  // Helper functions
  // ===================================================================

  const resetTemplateForm = () => {
    setTemplateForm({
      name: '',
      use_case: 'pilot_assessment', // Default to most common template type
      role: 'user',
      template: '',
      version: 'v1.0',
      description: '',
      is_active: true,
    });
  };

  const openEditModal = (template: PromptTemplate) => {
    setSelectedTemplate(template);
    setTemplateForm({
      name: template.name,
      use_case: template.use_case,
      role: template.role,
      template: template.template,
      version: template.version,
      description: template.description || '',
      is_active: template.is_active,
    });
    setShowEditModal(true);
  };

  const openPreviewModal = (template: PromptTemplate) => {
    setSelectedTemplate(template);
    setShowPreviewModal(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Template copied to clipboard!');
  };

  // Check if a template is a SYSTEM (global) template
  const isSystemTemplate = (template: PromptTemplate): boolean => {
    return template.org_id === SYSTEM_ORG_ID;
  };

  // Clone a SYSTEM template to create org-specific version
  const handleCloneTemplate = async (template: PromptTemplate) => {
    if (!user) {
      alert('Please ensure you are logged in.');
      return;
    }

    if (!organizationId) {
      alert('Organization ID not loaded. Please refresh the page.');
      return;
    }

    if (confirm(`Clone "${template.name}" as an organization-specific template? This will create a customizable copy for your airline.`)) {
      setSaving(true);

      try {
        const { error } = await supabase
          .from('prompt_templates')
          .insert({
            name: `${template.name} (Custom)`,
            use_case: template.use_case,
            role: template.role,
            template: template.template,
            version: `${template.version}-custom`,
            description: `Custom version of: ${template.description || template.name}`,
            is_active: false, // Create as inactive initially
            created_by: user.id,
            org_id: organizationId, // User's organization ID
          });

        if (error) {
          console.error('[PromptTemplatesAdmin] Clone error:', error);
          alert('Failed to clone template.');
          return;
        }

        alert(`Template cloned successfully! You can now customize "${template.name} (Custom)" for your airline.`);
        loadTemplates();
      } catch (error) {
        console.error('[PromptTemplatesAdmin] Clone error:', error);
        alert('An unexpected error occurred during cloning.');
      } finally {
        setSaving(false);
      }
    }
  };

  // ===================================================================
  // Render
  // ===================================================================

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-navy">Prompt Templates</h2>
          <p className="text-steel mt-1">
            Manage AI prompt templates for assessment generation, SOP Q&A, and event detection
          </p>
        </div>
        <button
          onClick={() => {
            resetTemplateForm();
            setShowCreateModal(true);
          }}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-orca-elevate text-white rounded-lg hover:bg-orca-elevate/90 transition sm:w-auto"
        >
          <Plus size={20} />
          <span>Create Template</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-steel" size={20} />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white text-navy placeholder-steel/50 border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
            />
          </div>

          {/* Use Case Filter */}
          <select
            value={filters.use_case}
            onChange={(e) => setFilters({ ...filters, use_case: e.target.value })}
            className="px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
          >
            <option value="all">All Use Cases</option>
            <optgroup label="Assessment Templates">
              <option value="pilot_evidence_extraction">🔍 Pilot Evidence Extraction (Pass 1)</option>
              <option value="pilot_assessment">📊 Pilot Assessment (Pass 2)</option>
              <option value="rca_analysis">🔬 Root-Cause Analysis (Pass 2.5)</option>
              <option value="smart_goals">🎯 SMART Improvement Goals (Pass 2.6)</option>
              <option value="instructor_assessment">👨‍🏫 Instructor Assessment</option>
            </optgroup>
            <optgroup label="Other Templates">
              <option value="evidence_validation">✅ Evidence Validation</option>
              <option value="sop_qa">📖 SOP Q&A</option>
              <option value="event_detection">🚨 Event Detection</option>
            </optgroup>
          </select>

          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Template List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="animate-spin text-orca-elevate" size={32} />
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <FileText className="mx-auto text-steel/50" size={48} />
          <p className="text-steel mt-4">No prompt templates found. Create your first template to get started!</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-lg shadow-md overflow-x-auto">
          <table className="w-full">
            <thead className="bg-navy text-white">
              <tr>
                <th className="px-2 py-2 text-left text-sm">Template Name</th>
                <th className="px-2 py-2 text-left text-sm">Use Case</th>
                <th className="px-2 py-2 text-left text-sm">Role</th>
                <th className="px-2 py-2 text-left text-sm">Version</th>
                <th className="px-2 py-2 text-left text-sm">Last Updated</th>
                <th className="px-2 py-2 text-center text-sm">Status</th>
                <th className="px-2 py-2 text-center text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTemplates.map((template) => (
                <tr key={template.id} className="border-b border-steel/20 hover:bg-white transition">
                  <td className="px-2 py-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm">{template.name}</p>
                        {isSystemTemplate(template) && (
                          <span
                            className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-semibold"
                            title="Global system template - Read-only"
                          >
                            <Globe size={12} />
                            GLOBAL
                          </span>
                        )}
                      </div>
                      {template.description && (
                        <p className="text-xs text-steel mt-0.5">{template.description}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-steel text-sm">
                    {TEMPLATE_INFO[template.use_case]?.label || template.use_case.replace('_', ' ').toUpperCase()}
                  </td>
                  <td className="px-2 py-2 text-steel capitalize text-sm">{template.role}</td>
                  <td className="px-2 py-2 text-steel text-sm">{template.version}</td>
                  <td className="px-2 py-2 text-steel text-xs">
                    {new Date(template.updated_at).toLocaleString()}
                  </td>
                  <td className="px-2 py-2 text-center">
                    {isSystemTemplate(template) ? (
                      /* SYSTEM templates cannot be deactivated */
                      <span title="Global templates are always active">
                        <CheckCircle className="text-blue-600" size={18} />
                      </span>
                    ) : (
                      <button
                        onClick={(e) => handleToggleActive(template, e)}
                        className="p-1 hover:bg-steel/10 rounded"
                        title={template.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {template.is_active ? (
                          <CheckCircle className="text-green-600" size={18} />
                        ) : (
                          <XCircle className="text-red-600" size={18} />
                        )}
                      </button>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => openPreviewModal(template)}
                        className="p-1 hover:bg-steel/10 rounded transition"
                        title="Preview"
                      >
                        <Eye size={16} className="text-steel" />
                      </button>

                      {/* Show Clone button for SYSTEM templates, Edit for org templates */}
                      {isSystemTemplate(template) ? (
                        <button
                          onClick={() => handleCloneTemplate(template)}
                          className="p-1 hover:bg-green-100 rounded transition"
                          title="Clone & Customize for your airline"
                        >
                          <Copy size={16} className="text-green-600" />
                        </button>
                      ) : (
                        <button
                          onClick={() => openEditModal(template)}
                          className="p-1 hover:bg-orca-elevate/10 rounded transition"
                          title="Edit"
                        >
                          <Edit size={16} className="text-orca-elevate" />
                        </button>
                      )}

                      <button
                        onClick={() => copyToClipboard(template.template)}
                        className="p-1 hover:bg-steel/10 rounded transition"
                        title="Copy to clipboard"
                      >
                        <Copy size={16} className="text-steel" />
                      </button>

                      {/* Only show delete for org-specific templates */}
                      {!isSystemTemplate(template) && (
                        <button
                          onClick={() => handleDelete(template)}
                          className="p-1 hover:bg-red-100 rounded transition"
                          title="Delete"
                        >
                          <Trash2 size={16} className="text-red-600" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {filteredTemplates.map((template) => (
              <div key={template.id} className="bg-white rounded-xl shadow-sm border border-steel/20 overflow-hidden">
                {/* Header */}
                <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-2">
                  <div className="flex items-start gap-2 min-w-0">
                    <FileText size={16} className="text-orca-elevate shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm text-navy">{template.name}</p>
                        {isSystemTemplate(template) && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            <Globe size={10} /> Global
                          </span>
                        )}
                      </div>
                      {template.description && <p className="text-xs text-steel mt-0.5 line-clamp-1">{template.description}</p>}
                    </div>
                  </div>
                  {isSystemTemplate(template) ? (
                    <CheckCircle className="text-blue-500 shrink-0" size={20} title="Always active" />
                  ) : (
                    <button onClick={(e) => handleToggleActive(template, e)} className="shrink-0 p-1 rounded-full hover:bg-steel/10" title={template.is_active ? 'Deactivate' : 'Activate'}>
                      {template.is_active ? <CheckCircle className="text-green-500" size={20} /> : <XCircle className="text-red-400" size={20} />}
                    </button>
                  )}
                </div>

                {/* Meta */}
                <div className="flex flex-col gap-1.5 px-4 pb-3 text-xs text-steel">
                  <span className="flex items-center gap-2">
                    <Loader size={13} className="text-steel/50 shrink-0" />
                    {TEMPLATE_INFO[template.use_case]?.label || template.use_case.replace('_', ' ')}
                  </span>
                  <span className="flex items-center gap-2">
                    <Users size={13} className="text-steel/50 shrink-0" />
                    <span className="capitalize">{template.role}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <Tag size={13} className="text-steel/50 shrink-0" />
                    {template.version}
                  </span>
                  <span className="flex items-center gap-2">
                    <Clock size={13} className="text-steel/50 shrink-0" />
                    {new Date(template.updated_at).toLocaleDateString()}
                  </span>
                </div>

                {/* Action bar */}
                <div className="border-t border-steel/10 flex divide-x divide-steel/10">
                  <button onClick={() => openPreviewModal(template)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-steel hover:bg-steel/5 transition">
                    <Eye size={14} /> Preview
                  </button>
                  {isSystemTemplate(template) ? (
                    <button onClick={() => handleCloneTemplate(template)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-green-600 hover:bg-green-50 transition">
                      <Copy size={14} /> Clone
                    </button>
                  ) : (
                    <button onClick={() => openEditModal(template)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-orca-elevate hover:bg-orca-elevate/5 transition">
                      <Edit size={14} /> Edit
                    </button>
                  )}
                  <button onClick={() => copyToClipboard(template.template)} className="flex items-center justify-center px-4 py-2.5 text-steel hover:bg-steel/5 transition" title="Copy to clipboard">
                    <Copy size={14} />
                  </button>
                  {!isSystemTemplate(template) && (
                    <button onClick={() => handleDelete(template)} className="flex items-center justify-center px-4 py-2.5 text-red-400 hover:bg-red-50 transition" title="Delete">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-steel/20">
              <h3 className="text-2xl font-bold text-navy">Create Prompt Template</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="e.g., Custom Assessment Prompt for Boeing 737"
                  className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Use Case *
                  </label>
                  <select
                    value={templateForm.use_case}
                    onChange={(e) => setTemplateForm({ ...templateForm, use_case: e.target.value as PromptTemplate['use_case'] })}
                    className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
                  >
                    <optgroup label="Assessment Templates">
                      <option value="pilot_evidence_extraction">🔍 Pilot Evidence Extraction (Pass 1)</option>
                      <option value="pilot_assessment">📊 Pilot Assessment (Pass 2)</option>
                      <option value="rca_analysis">🔬 Root-Cause Analysis (Pass 2.5)</option>
                      <option value="smart_goals">🎯 SMART Improvement Goals (Pass 2.6)</option>
                      <option value="instructor_assessment">👨‍🏫 Instructor Assessment</option>
                    </optgroup>
                    <optgroup label="Other Templates">
                      <option value="evidence_validation">✅ Evidence Validation</option>
                      <option value="sop_qa">📖 SOP Q&A</option>
                      <option value="event_detection">🚨 Event Detection</option>
                    </optgroup>
                    <optgroup label="Transcription Templates">
                      <option value="transcription_briefing">🎙️ Briefing Phase</option>
                      <option value="transcription_sim_main">🎙️ Simulator Main</option>
                      <option value="transcription_sim_tail">🎙️ Simulator Tail</option>
                      <option value="transcription_debriefing">🎙️ Debriefing Phase</option>
                      <option value="transcription_voice_passport">🎙️ Voice Passport</option>
                    </optgroup>
                  </select>
                  {/* Help text for selected use case */}
                  {templateForm.use_case && TEMPLATE_INFO[templateForm.use_case] && (
                    <div className="mt-2 p-3 bg-orca-elevate/10 border border-orca-elevate/30 rounded-lg">
                      <p className="text-sm text-navy font-semibold mb-1">
                        {TEMPLATE_INFO[templateForm.use_case]?.label}
                      </p>
                      <p className="text-xs text-steel mb-2">
                        {TEMPLATE_INFO[templateForm.use_case]?.description}
                      </p>
                      <div className="text-xs text-steel">
                        <strong>Available Variables:</strong> {TEMPLATE_INFO[templateForm.use_case]?.variables.join(', ')}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Role *
                  </label>
                  <select
                    value={templateForm.role}
                    onChange={(e) => setTemplateForm({ ...templateForm, role: e.target.value as PromptTemplate['role'] })}
                    className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
                  >
                    <option value="system">System</option>
                    <option value="user">User</option>
                    <option value="tool">Tool</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Version
                  </label>
                  <input
                    type="text"
                    value={templateForm.version}
                    onChange={(e) => setTemplateForm({ ...templateForm, version: e.target.value })}
                    placeholder="v1.0"
                    className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">
                  Description
                </label>
                <textarea
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                  placeholder="Brief description of what this template does..."
                  rows={2}
                  className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">
                  Template Content *
                  <span className="text-steel text-xs font-normal ml-2">
                    (Supports Handlebars: {`{{airline_name}}, {{sop_context}}, {{competency}}`})
                  </span>
                </label>
                <textarea
                  value={templateForm.template}
                  onChange={(e) => setTemplateForm({ ...templateForm, template: e.target.value })}
                  placeholder="Enter your prompt template here..."
                  rows={12}
                  className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate font-mono text-sm"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active_create"
                  checked={templateForm.is_active}
                  onChange={(e) => setTemplateForm({ ...templateForm, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_active_create" className="text-sm text-navy">
                  Activate immediately
                </label>
              </div>
            </div>
            <div className="p-6 border-t border-steel/20 flex items-center justify-end gap-4">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetTemplateForm();
                }}
                className="px-4 py-2 text-steel hover:bg-steel/10 rounded-lg transition"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                className="px-4 py-2 bg-orca-elevate text-white rounded-lg hover:bg-orca-elevate/90 transition disabled:opacity-50"
                disabled={saving}
              >
                {saving ? 'Creating...' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-steel/20">
              <h3 className="text-2xl font-bold text-navy">Edit Prompt Template</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Use Case *
                  </label>
                  <select
                    value={templateForm.use_case}
                    onChange={(e) => setTemplateForm({ ...templateForm, use_case: e.target.value as PromptTemplate['use_case'] })}
                    className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
                  >
                    <optgroup label="Assessment Templates">
                      <option value="pilot_evidence_extraction">🔍 Pilot Evidence Extraction (Pass 1)</option>
                      <option value="pilot_assessment">📊 Pilot Assessment (Pass 2)</option>
                      <option value="rca_analysis">🔬 Root-Cause Analysis (Pass 2.5)</option>
                      <option value="smart_goals">🎯 SMART Improvement Goals (Pass 2.6)</option>
                      <option value="instructor_assessment">👨‍🏫 Instructor Assessment</option>
                    </optgroup>
                    <optgroup label="Other Templates">
                      <option value="evidence_validation">✅ Evidence Validation</option>
                      <option value="sop_qa">📖 SOP Q&A</option>
                      <option value="event_detection">🚨 Event Detection</option>
                    </optgroup>
                    <optgroup label="Transcription Templates">
                      <option value="transcription_briefing">🎙️ Briefing Phase</option>
                      <option value="transcription_sim_main">🎙️ Simulator Main</option>
                      <option value="transcription_sim_tail">🎙️ Simulator Tail</option>
                      <option value="transcription_debriefing">🎙️ Debriefing Phase</option>
                      <option value="transcription_voice_passport">🎙️ Voice Passport</option>
                    </optgroup>
                  </select>
                  {/* Help text for selected use case */}
                  {templateForm.use_case && TEMPLATE_INFO[templateForm.use_case] && (
                    <div className="mt-2 p-3 bg-orca-elevate/10 border border-orca-elevate/30 rounded-lg">
                      <p className="text-sm text-navy font-semibold mb-1">
                        {TEMPLATE_INFO[templateForm.use_case]?.label}
                      </p>
                      <p className="text-xs text-steel mb-2">
                        {TEMPLATE_INFO[templateForm.use_case]?.description}
                      </p>
                      <div className="text-xs text-steel">
                        <strong>Available Variables:</strong> {TEMPLATE_INFO[templateForm.use_case]?.variables.join(', ')}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Role *
                  </label>
                  <select
                    value={templateForm.role}
                    onChange={(e) => setTemplateForm({ ...templateForm, role: e.target.value as PromptTemplate['role'] })}
                    className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
                  >
                    <option value="system">System</option>
                    <option value="user">User</option>
                    <option value="tool">Tool</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Version
                  </label>
                  <input
                    type="text"
                    value={templateForm.version}
                    onChange={(e) => setTemplateForm({ ...templateForm, version: e.target.value })}
                    className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">
                  Description
                </label>
                <textarea
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">
                  Template Content *
                  <span className="text-steel text-xs font-normal ml-2">
                    (Supports Handlebars: {`{{airline_name}}, {{sop_context}}, {{competency}}`})
                  </span>
                </label>
                <textarea
                  value={templateForm.template}
                  onChange={(e) => setTemplateForm({ ...templateForm, template: e.target.value })}
                  rows={12}
                  className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate font-mono text-sm"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active_edit"
                  checked={templateForm.is_active}
                  onChange={(e) => setTemplateForm({ ...templateForm, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_active_edit" className="text-sm text-navy">
                  Active
                </label>
              </div>
            </div>
            <div className="p-6 border-t border-steel/20 flex items-center justify-end gap-4">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedTemplate(null);
                  resetTemplateForm();
                }}
                className="px-4 py-2 text-steel hover:bg-steel/10 rounded-lg transition"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="px-4 py-2 bg-orca-elevate text-white rounded-lg hover:bg-orca-elevate/90 transition disabled:opacity-50"
                disabled={saving}
              >
                {saving ? 'Updating...' : 'Update Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-steel/20">
              <h3 className="text-2xl font-bold text-navy">{selectedTemplate.name}</h3>
              <div className="flex items-center gap-4 mt-2 text-sm text-steel">
                <span>Use Case: <strong>{selectedTemplate.use_case}</strong></span>
                <span>Role: <strong>{selectedTemplate.role}</strong></span>
                <span>Version: <strong>{selectedTemplate.version}</strong></span>
                <span>
                  Status:
                  <strong className={selectedTemplate.is_active ? 'text-green-600' : 'text-red-600'}>
                    {selectedTemplate.is_active ? ' Active' : ' Inactive'}
                  </strong>
                </span>
              </div>
              {selectedTemplate.description && (
                <p className="mt-2 text-steel">{selectedTemplate.description}</p>
              )}
            </div>
            <div className="p-6">
              <label className="block text-sm font-semibold text-navy mb-2">
                Template Content
              </label>
              <pre className="bg-white p-4 rounded-lg text-sm font-mono overflow-x-auto whitespace-pre-wrap">
                {selectedTemplate.template}
              </pre>
            </div>
            <div className="p-6 border-t border-steel/20 flex items-center justify-end gap-4">
              <button
                onClick={() => copyToClipboard(selectedTemplate.template)}
                className="px-4 py-2 text-orca-elevate hover:bg-orca-elevate/10 rounded-lg transition"
              >
                Copy to Clipboard
              </button>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy/90 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


